import React from "react";
import type { SingleValue } from "react-select";
import t from "../../i18n";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import { emptyOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { ApiValue } from "./formHelpers";
import { getDefaultValue, multipleValue } from "./formHelpers";
import type { Project } from "../../api/useProjects";
import { uniq } from "diagnostics-lib/array";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../../api/useCategories";
import { useTranslations } from "../../api/useTranslations";

// EquipmentType (unique with full option)
export const useProjectTypeInput = (selectedProjects: Project[]) => {
  const translations = useTranslations();
  const [isDirty, setIsDirty] = React.useState(false);
  const projectTypes = useCategories("PROJECT_TYPE");
  const createProjectType = useCreateCategory("PROJECT_TYPE");
  const updateProjectType = useUpdateCategory("PROJECT_TYPE");
  const deleteProjectType = useDeleteCategory("PROJECT_TYPE");

  const typeFromSelection = uniq(
    selectedProjects.map(project => ({
      id: project.typeId ?? null,
      name: project.typeId ?? "",
    })),
  );

  const defaultValue = getDefaultValue<ApiValue>(typeFromSelection, "id", projectType => ({
    label: projectType.name,
    value: String(projectType.id),
  }));

  const projectTypeOptions = [
    { id: emptyOption.value, name: emptyOption.label },
    ...projectTypes.map(c => ({ id: c, name: c })),
  ];

  const [defaultTypeOption, setDefaultTypeOption] = React.useState(() => {
    if (defaultValue?.label === "" || defaultValue?.label === multipleValue.label) return null;
    return defaultValue;
  });

  const [selectedProjectTypes, setSelectedProjectTypes] =
    React.useState<SingleValue<BaseOption>>(defaultTypeOption);

  const handleProjectTypeChanged = React.useCallback((option: SingleValue<BaseOption>) => {
    setSelectedProjectTypes(option);
  }, []);

  React.useEffect(() => {
    if (selectedProjectTypes?.value === defaultTypeOption?.value) {
      setIsDirty(false);
    } else {
      setIsDirty(true);
    }
  }, [selectedProjectTypes, defaultTypeOption]);

  const resetValue = React.useCallback(() => {
    setSelectedProjectTypes(defaultTypeOption);
    setIsDirty(false);
  }, [defaultTypeOption]);

  const handleProjectTypeCreated = React.useCallback(
    async (newProjectType: string) => {
      await createProjectType(newProjectType);
      setIsDirty(true);

      setSelectedProjectTypes({
        label: newProjectType,
        value: newProjectType,
      });
    },
    [createProjectType],
  );

  const handleProjectTypeEdited = React.useCallback(
    async (option: BaseOption, label: string) => {
      if (!option.value) return;
      await updateProjectType(option.value, label);
      if (selectedProjectTypes?.value === option.value) {
        setSelectedProjectTypes({ ...option, label });
      }
    },
    [updateProjectType, setSelectedProjectTypes, selectedProjectTypes],
  );

  const handleProjectTypeDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (!option.value) return;
      await deleteProjectType(option.value);
      if (selectedProjectTypes?.value === option.value) {
        setDefaultTypeOption(null);
        setSelectedProjectTypes(emptyOption);
      }
    },
    [deleteProjectType, selectedProjectTypes?.value],
  );

  const placeholder = React.useMemo(
    () =>
      defaultValue
        ? defaultValue.value === multipleValue.value
          ? t("projects.modal.multiple_value")
          : t("form.no_value")
        : t("projects.modal.create_or_edit.input.type", translations),
    [defaultValue, translations],
  );

  return {
    placeholder,
    projectTypeOptions,
    selectedProjectTypes,
    setSelectedProjectTypes,
    handleProjectTypeChanged,
    handleProjectTypeCreated,
    handleProjectTypeEdited,
    handleProjectTypeDeleted,
    isDirty,
    resetValue,
  };
};
